import {
  DefaultButton,
  IIconProps,
  Image,
  Link,
  Panel,
  Persona,
  PersonaSize,
  PanelType,
  Pivot,
  PivotLinkSize,
  PivotLinkFormat,
  PivotItem,
  IPivotStyles,
  IStyleSet,
  Stack,
  mergeStyleSets,
} from "@fluentui/react";
import { mergeStyles } from "office-ui-fabric-react";
import { ActivityItem, IActivityItemProps } from "office-ui-fabric-react";
import {
  Dropdown,
  IDropdownOption,
  IDropdownStyles,
} from "office-ui-fabric-react/lib/Dropdown";
import React, { FC, useState, useRef, useEffect } from "react";
import Moment from "react-moment";
import clsx from "clsx";
import moment from "moment";
import { useBoolean } from "@uifabric/react-hooks";
import {
  Comment,
  DailyReport,
  DailyReportNomination,
  DailyReportActivity,
  LongDockLogReport,
} from "../../models";
import SofIcon from "../common/Icons";
import { pushNotification } from "../../api/pushNotification";
import { Get } from "../../api/apiUtils";
import { Option } from "../../models/Option";

type Props = {
  dailyReport: DailyReport;
  setSelectedFileUrl: React.Dispatch<React.SetStateAction<string>>;
  setSelectedFileName: React.Dispatch<React.SetStateAction<string>>;
  setPdfContentBlob: React.Dispatch<React.SetStateAction<Blob | undefined>>;
};

const classes = mergeStyleSets({
  tab: {
    borderBottom: "1px solid #E2E6E8",
  },
  detailsDiv: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    "-webkit-line-clamp": "1",
    display: "-webkit-box",
    "-webkit-box-orient": "vertical",
  },
  contentPanel: {
    flex: "1 1 auto",
    border: "1px solid #E2E6E8",
    overflowY: "auto",
    minHeight: "500px",
    padding: "10px",
  },
  inlineBlockDiv: {
    display: "inline-block",
    paddingRight: "10px",
    width: "40px",
  },
  bottomMarginDiv: {
    marginBottom: "4px",
  },
  emptyBigDiv: {
    color: "#B8B8B8",
    fontSize: "18px",
    fontWeight: "bold",
  },
  roundBgDiv: {
    width: "70px",
    height: "70px",
    borderRadius: "35px",
    backgroundColor: "#E2E6E8",
    display: "block",
    padding: "22px",
    margin: "1% 44%",
  },
  zeroCommentDiv: {
    width: "100%",
    textAlign: "center",
  },
  commentTextDiv: {
    whiteSpace: "pre-wrap",
  },
  commentBtn: {
    position: "absolute",
    right: "10px",
    marginTop: "30px",
  },
  tableHeader: {
    backgroundColor: "black",
    color: "#FFFFFF",
  },
  panelCommentHeader: {
    fontSize: "15px",
    fontWeight: "bold",
    color: "#009FDF",
    borderBottom: "solid",
    borderColor: "#009FDF",
    width: "85px",
    borderWidth: "0.5px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(500px,1fr))",
    gridGap: "0.5em",
    textAlign: "center",
  },
  gridCard: {
    padding: 20,
    borderStyle: "solid",
    borderColor: "lightgray",
    borderWidth: "thin",
  },
  formContainer: { display: "flex", flexDirection: "column", height: "100%" },
  formHeader: {
    gap: 8,
    padding: 16,
    display: "flex",
    alignItems: "center",
    backgroundColor: "white",
  },
  toolbarSeparator: {
    height: "40px",
    width: "1px",
    background: "#D8DFE6",
    margin: "0",
  },
  printContainer: {
    flexGrow: 1,
    overflow: "auto",
    display: "flex",
  },
});
const buttonIcon = mergeStyles({
  padding: "9px 10px",
  fontSize: 16,
  fontWeight: 500,
  height: 40,
  width: 40,
  justifyContent: "center",
  border: "1px solid #D8DFE6",
  borderRadius: "2px",
  display: "flex",
  alignItems: "center",
  color: "#4c4c4c",
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0",
    },
  },
});

const pivotStyles: Partial<IStyleSet<IPivotStyles>> = {
  link: {
    minWidth: "150px",
    selectors: {
      ":hover": {
        backgroundColor: "#EEF0F8",
        color: "#323130",
      },
      ":active": {
        backgroundColor: "#EEF0F8",
        color: "#323130",
      },
    },
  },
  linkIsSelected: {
    minWidth: "150px",
    backgroundColor: "#EEF0F8",
    color: "#323130",
    selectors: {
      ":hover": {
        backgroundColor: "#EEF0F8",
        color: "#323130",
      },
      ":active": {
        backgroundColor: "#EEF0F8",
        color: "#323130",
      },
    },
  },
};

const dropdownStyles: Partial<IDropdownStyles> = {
  dropdown: { width: 300 },
  dropdownItemSelected: {
    selectors: {
      ":focus, :hover": { outline: "0 !important" },
    },
  },
  dropdownItem: {
    selectors: {
      ":focus, :hover": { outline: "0 !important" },
    },
  },
  dropdownItemsWrapper: { overflow: "hidden" },
};

const commentIcon: IIconProps = {
  iconName: "Chat",
  style: {
    fontSize: "18px",
    color: "#212529",
  },
};

const ReportDetails: FC<Props> = (props: Props) => {
  const [selectedNominationNo, setSelectedNominationNo] = useState<string>("0");
  const [selectedNomination, setSelectedNomination] =
    useState<DailyReportNomination>();
  const [activities, setActivities] = useState<DailyReportActivity[]>(
    props.dailyReport.lineHandlingActivities
  );
  const [longDockLogReport, setlongDockLogReport] = useState<LongDockLogReport>(
    props.dailyReport.longDockLogReport
  );
  const [comments, setComments] = useState<Comment[]>(
    props.dailyReport.comments
  );
  const [expandedNomination, setExpandedNomination] = useState<string>("");
  const [expandedActivity, setExpandedActivity] = useState<string>("");
  const commentEnd = useRef<HTMLDivElement>(null);
  const [files, setFiles] = useState<any[]>([]);

  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] =
    useBoolean(false);

  const onRenderFooterContent = React.useCallback(
    () => (
      <div>
        <DefaultButton onClick={dismissPanel}>Close</DefaultButton>
      </div>
    ),
    [dismissPanel]
  );

  const getOptions = (dailyReport: DailyReport) => {
    const options: IDropdownOption[] = [];
    options.push({
      key: "0",
      text: `Line Handling # ${dailyReport.lineHandlingNo.substr(
        dailyReport.lineHandlingNo.length - 6
      )}`,
      selected: true,
      title: `${dailyReport.vehicleName}`,
    });

    dailyReport.nominations.map((nomination) => {
      options.push({
        key: nomination.nominationNo,
        text: `Nomination # ${nomination.nominationNo.substr(
          nomination.nominationNo.length - 5
        )}`,
        title: `${nomination.product}`,
      });

      return options;
    });

    return options;
  };

  const expandDiv = (nominationNo: string) => {
    if (expandedNomination.indexOf(nominationNo) !== -1) {
      let expandedStr = expandedNomination;
      expandedStr = expandedStr.replace(nominationNo, "");

      setExpandedNomination(expandedStr);
    } else {
      setExpandedNomination(`${expandedNomination}${nominationNo}`);
    }
  };

  const expandActivityDiv = (id: number) => {
    let activityId = `[${id}]`;

    if (expandedActivity.indexOf(activityId) !== -1) {
      let expandedStr = expandedActivity;
      expandedStr = expandedStr.replace(activityId, "");

      setExpandedActivity(expandedStr);
    } else {
      setExpandedActivity(`${expandedActivity}${activityId}`);
    }
  };

  const commentActivities = comments.map(
    (c, index: number) =>
      ({
        key: index,
        activityDescription: [
          <table>
            <tr>
              <td rowSpan={2} style={{ verticalAlign: "top" }}>
                <span className={classes.inlineBlockDiv}>
                  {c.profilePicture && (
                    <Image
                      style={{
                        borderRadius: "50%",
                        height: "auto",
                      }}
                      src={`data:image/jpeg;base64, ${c.profilePicture}`}
                      width="30"
                      height="30"
                    />
                  )}
                  {!c.profilePicture && (
                    <Persona
                      size={PersonaSize.size28}
                      initialsColor={"#4C4C4C"}
                      color={"#4C4C4C"}
                      imageInitials={c.submittedByName
                        .split(" (")[0]
                        .split(" ")
                        .map((n, i, a) =>
                          i === 0 || i + 1 === a.length ? n[0] : null
                        )
                        .join("")}
                    />
                  )}
                </span>
              </td>
              <td>
                <b key={`submittedby-${index}`}>{c.submittedByName}</b>&nbsp;
                <Moment format="HH:mm - ll">
                  {moment.utc(c.submittedOn).local().toDate()}
                </Moment>
              </td>
            </tr>

            <tr>
              <td>
                <div className={classes.commentTextDiv}>{c.commentText}</div>
              </td>
            </tr>
          </table>,
        ],
      } as IActivityItemProps | { key: number })
  );

  useEffect(() => {
    pushNotification.connection.off("ReportUpdated");

    pushNotification.connection.on("ReportUpdated", (message) => {
      var result = message as DailyReport;
      var lineHandlingNo = result.lineHandlingNo.toUpperCase();
      var cnt = 0;
      if (props.dailyReport.lineHandlingNo === lineHandlingNo && cnt === 0) {
        setlongDockLogReport(result.longDockLogReport);

        if (selectedNomination === undefined) {
          setComments(result.comments);
          setActivities(result.lineHandlingActivities);
        } else {
          var nomination = result.nominations.find(
            (n) =>
              n.nominationNo.toUpperCase() ===
              selectedNominationNo.toUpperCase()
          );

          if (nomination !== undefined) {
            setComments(nomination.comments);
            setActivities(nomination.nominationActivities);
          }
        }

        cnt += 1;
      }
    });
  }, [props.dailyReport, selectedNomination]);

  const loadFiles = async () => {
    const response = await Get(
      `/api/Attachment/marine/reports/${props.dailyReport.lineHandlingNo}`
    );
    const resultRes = await response.json();
    const attachments = await Promise.all(
      (resultRes || []).map(async (file: any) => {
        //const base64Data = await getBase64FromUrl(file.url);
        return {
          attachmentId: file.attachmentId,
          //previewBase64: base64Data,
          previewBlob: file.url,
          name: file.fileName,
          size: file.fileSize,
          canDelete: file.canDelete,
        };
      })
    );
    return attachments;
  };
  const getBase64FromUrl = async (url: string) => {
    const existingPdfBytes = await fetch(url).then((res) => res.arrayBuffer());
    const blob = new Blob([existingPdfBytes], { type: "application/pdf" });
    props.setPdfContentBlob(blob);
    return URL.createObjectURL(blob);
  };

  const onPreview = async (
    e: React.MouseEvent<HTMLElement>,
    url: string,
    fileName: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    props.setSelectedFileName("");
    const modal = document.getElementById("preview") as HTMLDialogElement;
    modal.showModal();

    var file = await getBase64FromUrl(url);
    props.setSelectedFileUrl(file);
    props.setSelectedFileName(fileName);
  };

  useEffect(() => {
    const handleFiles = async () => {
      const data = await loadFiles();
      setFiles(data ?? []);
    };
    handleFiles();
  }, []);

  return (
    <div>
      <Pivot
        className={classes.tab}
        linkFormat={"tabs"}
        styles={pivotStyles}
        linkSize={"normal"}
      >
        <PivotItem key="Summary" itemKey="Summary" headerText="Summary">
          <div style={{ margin: "15px 15px 15px 5px" }}>
            Dock Number: <b>{longDockLogReport.dockNumber}</b>
          </div>
          <table className="tbl-detail">
            <tbody>
              <tr>
                <td>
                  <dt>Arrived Along Side</dt>
                  <dd>
                    {props.dailyReport.longDockLogReport.arrivedAlongSide && (
                      <Moment
                        format="ll HH:mm"
                        local
                      >{`${longDockLogReport.arrivedAlongSide} UTC`}</Moment>
                    )}
                  </dd>
                </td>
                <td>
                  <dt>Gangway On Vessel</dt>
                  <dd>
                    {props.dailyReport.longDockLogReport.gangwayOnVessel && (
                      <Moment
                        format="ll HH:mm"
                        local
                      >{`${longDockLogReport.gangwayOnVessel} UTC`}</Moment>
                    )}
                  </dd>
                </td>
                <td>
                  <dt>Pre-transfer Conf. Start</dt>
                  <dd>
                    {props.dailyReport.longDockLogReport
                      .preTransferConferenceStart && (
                      <Moment
                        format="ll HH:mm"
                        local
                      >{`${longDockLogReport.preTransferConferenceStart} UTC`}</Moment>
                    )}
                  </dd>
                </td>
                <td>
                  <dt>Pre-transfer Conf. Finish</dt>
                  <dd>
                    {props.dailyReport.longDockLogReport
                      .preTransferConferenceFinish && (
                      <Moment
                        format="ll HH:mm"
                        local
                      >{`${longDockLogReport.preTransferConferenceFinish} UTC`}</Moment>
                    )}
                  </dd>
                </td>
              </tr>
              <tr>
                <td>
                  <dt>First Hose On</dt>
                  <dd>
                    {longDockLogReport.firstHoseOn && (
                      <Moment
                        format="ll HH:mm"
                        local
                      >{`${longDockLogReport.firstHoseOn} UTC`}</Moment>
                    )}
                  </dd>
                </td>
                <td>
                  <dt>Last Hose Off</dt>
                  <dd>
                    {longDockLogReport.lastHoseOff && (
                      <Moment
                        format="ll HH:mm"
                        local
                      >{`${longDockLogReport.lastHoseOff} UTC`}</Moment>
                    )}
                  </dd>
                </td>
                <td>
                  <dt>Vessel Sailed</dt>
                  <dd>
                    {longDockLogReport.vesselSailed && (
                      <Moment
                        format="ll HH:mm"
                        local
                      >{`${longDockLogReport.vesselSailed} UTC`}</Moment>
                    )}
                  </dd>
                </td>
                <td>
                  <dt></dt>
                  <dd>{}&nbsp;</dd>
                </td>
              </tr>
            </tbody>
          </table>
          <table
            className="table table-sm"
            style={{ marginTop: "10px", tableLayout: "fixed" }}
          >
            <thead>
              <tr className={classes.tableHeader}>
                <th>Line #</th>
                <th>Tank</th>
                <th>Customer</th>
                <th>Product</th>
                <th>Vessel Tank#</th>
                <th>Quantity / MT</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {longDockLogReport.nominations.map((nomination) => (
                <>
                  <tr
                    style={{
                      background: `${
                        expandedNomination.indexOf(nomination.nominationNo) !==
                        -1
                          ? "#F2F4F6"
                          : ""
                      }`,
                    }}
                  >
                    <td>{nomination.ourLineNumber}</td>
                    <td>{nomination.tank}</td>
                    <td>{nomination.customer}</td>
                    <td>{nomination.product}</td>
                    <td>{nomination.vesselTankNumber}</td>
                    <td>{parseFloat(nomination.quantity).toFixed(2)}</td>
                    <td>
                      <Link onClick={() => expandDiv(nomination.nominationNo)}>
                        {expandedNomination.indexOf(nomination.nominationNo) !==
                        -1
                          ? "Hide Details"
                          : "Show Details"}
                      </Link>
                    </td>
                  </tr>

                  {expandedNomination.indexOf(nomination.nominationNo) !==
                    -1 && (
                    <tr>
                      <td colSpan={7}>
                        <table className="tbl-detail">
                          <tbody>
                            <tr>
                              <td>
                                <dt>Vapor Hose Connected</dt>
                                <dd>
                                  {nomination.vaporHoseConnected && (
                                    <Moment
                                      format="ll HH:mm"
                                      local
                                    >{`${nomination.vaporHoseConnected} UTC`}</Moment>
                                  )}
                                </dd>
                              </td>
                              <td>
                                <dt>Cargo Hose Connected</dt>
                                <dd>
                                  {nomination.cargoHoseConnected && (
                                    <Moment
                                      format="ll HH:mm"
                                      local
                                    >{`${nomination.cargoHoseConnected} UTC`}</Moment>
                                  )}
                                </dd>
                              </td>

                              <td>
                                <dt>Transfer Start</dt>

                                <dd>
                                  {" "}
                                  {nomination.transferStart && (
                                    <Moment
                                      format="ll HH:mm"
                                      local
                                    >{`${nomination.transferStart} UTC`}</Moment>
                                  )}
                                </dd>
                              </td>

                              <td>
                                {nomination.transferType !== "OverTheSide" && (
                                  <>
                                    <dt>Tank/Cargo Accepted</dt>

                                    <dd>
                                      {nomination.tankOrCargoAccepted && (
                                        <Moment
                                          format="ll HH:mm"
                                          local
                                        >{`${nomination.tankOrCargoAccepted} UTC`}</Moment>
                                      )}
                                    </dd>
                                  </>
                                )}
                              </td>
                            </tr>

                            <tr>
                              <td>
                                <dt>Vapor Hose Disconnected</dt>
                                <dd>
                                  {nomination.vaporHoseDisconnected && (
                                    <Moment
                                      format="ll HH:mm"
                                      local
                                    >{`${nomination.vaporHoseDisconnected} UTC`}</Moment>
                                  )}
                                </dd>
                              </td>
                              <td>
                                <dt>Cargo Hose Disconnected</dt>
                                <dd>
                                  {" "}
                                  {nomination.cargoHoseDisconnected && (
                                    <Moment
                                      format="ll HH:mm"
                                      local
                                    >{`${nomination.cargoHoseDisconnected} UTC`}</Moment>
                                  )}
                                </dd>
                              </td>
                              <td>
                                <dt>Transfer Finish</dt>
                                <dd>
                                  {nomination.transferFinish && (
                                    <Moment
                                      format="ll HH:mm"
                                      local
                                    >{`${nomination.transferFinish} UTC`}</Moment>
                                  )}
                                </dd>
                              </td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </PivotItem>
        <PivotItem key="Details" itemKey="Details" headerText="Details">
          <Stack horizontal>
            <Stack.Item grow>
              <Dropdown
                label="Nomination and Line Handling"
                options={getOptions(props.dailyReport)}
                styles={dropdownStyles}
                onChange={(e, selectedOption) => {
                  var nomination = props.dailyReport.nominations.find(
                    (n) => n.nominationNo === selectedOption!.key
                  );

                  if (nomination !== undefined) {
                    setSelectedNominationNo(nomination.nominationNo);
                    setSelectedNomination(nomination);
                    setActivities(nomination.nominationActivities);
                    setComments(nomination.comments);
                  } else {
                    setSelectedNominationNo("0");
                    setSelectedNomination(undefined);
                    setActivities(props.dailyReport.lineHandlingActivities);
                    setComments(props.dailyReport.comments);
                  }
                }}
                onRenderOption={(option) => {
                  return (
                    <div>
                      <span style={{ fontWeight: "bold" }}>{option!.text}</span>
                      <br />
                      <span className={classes.detailsDiv}>
                        {option!.title}
                      </span>
                    </div>
                  );
                }}
              />
            </Stack.Item>
            <Stack.Item grow>
              <DefaultButton
                text="View Comments"
                onClick={openPanel}
                className={classes.commentBtn}
                iconProps={commentIcon}
              />
            </Stack.Item>
          </Stack>

          {selectedNomination && (
            <table className="tbl-detail" style={{ marginTop: "10px" }}>
              <tbody>
                <tr>
                  <td>
                    <dt>Customer</dt>
                    <dd>{selectedNomination.customer}</dd>
                  </td>
                  <td>
                    <dt>Product</dt>
                    <dd>{selectedNomination.product}</dd>
                  </td>

                  <td>
                    <dt>Quantity</dt>
                    <dd>{selectedNomination.quantity}</dd>
                  </td>

                  <td>
                    <dt>Tank</dt>
                    <dd>{selectedNomination.tank}</dd>
                  </td>
                  <td>
                    <dt>Samples Count</dt>
                    <dd>{selectedNomination.samplingCount}</dd>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          <table className="table table-sm" style={{ marginTop: "10px" }}>
            <thead>
              <tr className={classes.tableHeader}>
                <th>Description</th>
                <th>Date</th>
                <th>Time</th>
                <th>Delay Reason</th>
                <th>Delay Duration</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {activities.map((activity) => (
                <>
                  <tr
                    style={{
                      backgroundColor: `${
                        activity.status === "Skipped" ? "#eeeeee" : ""
                      }`,
                    }}
                  >
                    <td
                      style={{
                        display: "flex",
                        borderLeft: `7px solid ${
                          activity.isSentToSAP ? "#4CAF50" : "#FF0000"
                        }`,
                      }}
                    >
                      {activity.description}
                    </td>
                    <td>
                      {activity.executionDate && (
                        <Moment format="ll" local>
                          {`${activity.executionDate} UTC`}
                        </Moment>
                      )}
                      {!activity.executionDate && "-"}
                    </td>
                    <td>
                      {activity.executionDate && (
                        <Moment format="HH:mm" local>
                          {`${activity.executionDate} UTC`}
                        </Moment>
                      )}
                      {!activity.executionDate && "-"}
                    </td>
                    <td style={{ maxWidth: "200px" }}>
                      {activity.delayReason && activity.delayReason}
                      {!activity.delayReason && "-"}
                    </td>
                    <td>
                      {activity.delayReason && activity.delayDuration}
                      {!activity.delayReason && "-"}
                    </td>
                    <td
                      style={{
                        color: `${activity.status === "Done" ? "#4CAF50" : ""}`,
                      }}
                    >
                      {activity.status}
                    </td>

                    <td>
                      {activity.inputValues.length > 0 && (
                        <Link onClick={() => expandActivityDiv(activity.id)}>
                          {expandedActivity.indexOf(`[${activity.id}]`) !== -1
                            ? "Hide Details"
                            : "Show Details"}
                        </Link>
                      )}
                    </td>
                  </tr>

                  {expandedActivity.indexOf(`[${activity.id}]`) !== -1 &&
                    activity.inputValues && (
                      <tr>
                        <td colSpan={8}>
                          <table className="tbl-detail">
                            <tbody>
                              <tr>
                                {activity.inputValues.map(
                                  (a, index: number) => (
                                    <td>
                                      <dt>{a.caption}</dt>

                                      <dd>{a.value}</dd>
                                    </td>
                                  )
                                )}
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                </>
              ))}
            </tbody>
          </table>
        </PivotItem>
        <PivotItem key="Services" itemKey="Services" headerText="Services">
          <div style={{ padding: 20 }}>
            <div className={classes.grid}>
              {files?.length > 0 ? (
                files?.map(
                  (a: { name: string; previewBlob: string }, index: number) => {
                    return (
                      <div
                        className={classes.gridCard}
                        key={`grid-card-${index}`}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "1em",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <div>
                              <div>
                                <SofIcon iconname="PDFFile" />
                              </div>
                              <div>
                                <a
                                  href="#"
                                  onClick={(e: React.MouseEvent<HTMLElement>) =>
                                    onPreview(e, a.previewBlob, a.name)
                                  }
                                >
                                  View
                                </a>
                              </div>
                            </div>
                          </div>
                          <div>{a.name}</div>
                        </div>
                      </div>
                    );
                  }
                )
              ) : (
                <p style={{ fontWeight: "bold", textAlign: "left" }}>
                  No details available
                </p>
              )}
            </div>
          </div>
        </PivotItem>
      </Pivot>

      <Panel
        isLightDismiss
        isOpen={isOpen}
        onDismiss={dismissPanel}
        closeButtonAriaLabel="Close"
        type={PanelType.custom}
        customWidth={"580px"}
        isFooterAtBottom={true}
        headerText={
          selectedNominationNo === "0"
            ? `Line Handling # ${props.dailyReport.lineHandlingNo.substr(
                props.dailyReport.lineHandlingNo.length - 5
              )}`
            : `Nomination # ${selectedNominationNo.substr(
                selectedNominationNo.length - 5
              )}`
        }
        onRenderFooterContent={onRenderFooterContent}
        styles={{
          headerText: { color: "#12427d" },
        }}
      >
        <div style={{ marginBottom: "10px" }}>
          {selectedNomination === undefined
            ? props.dailyReport.vehicleName
            : selectedNomination.customer}
        </div>

        <div className={classes.panelCommentHeader}>Comments</div>

        <div className={clsx(classes.contentPanel, "custom-scroll")}>
          {commentActivities.length === 0 && (
            <div className={classes.zeroCommentDiv}>
              <div className={classes.roundBgDiv}>
                <SofIcon iconname="NoComment" />
              </div>
              <div className={classes.emptyBigDiv}>No Comments Yet</div>
            </div>
          )}
          {commentActivities.length > 0 &&
            commentActivities.map((a, index: number) => (
              <ActivityItem
                key={`comment-${index}`}
                {...a}
                className={classes.bottomMarginDiv}
              />
            ))}
          <div id="comments-bottom" ref={commentEnd}></div>
        </div>
      </Panel>
    </div>
  );
};

export default ReportDetails;
